import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PrivateRoute from '../components/auth/PrivateRoute';
import Dashboard from '../pages/Dashboard';
import Login from '../pages/auth/Login';
import UserListPage from '../pages/user/UserListPage';
import CustomerListPage from '../pages/customer/CustomerListPage';
import CustomerOrderHistory from '../pages/customer/CustomerOrderHistory';
import UserCreatePage from '../pages/user/UserCreatePage';
import EditUser from '../pages/user/EditUser';
import OrderManagement from '../pages/order/OrderManagement';
import CreateOrder from '../pages/order/CreateOrder';
import CastOrderList from '../features/cast/CastOrderList';
import CastOrderEdit from '../features/cast/CastOrderEdit';
import DriverOrderList from '../features/driver/DriverOrderList';
import SystemSettingsManagement from '../pages/SystemSettingsManagement';
import SystemSettingsPage from '../features/settings/SystemSettings';
import Shifts from '../pages/Shifts';
import NotificationSettings from '../pages/notification/NotificationSettings';
// import ShiftCalendar from '../features/shifts/ShiftCalendar';
// import ShiftManagement from '../features/shifts/ShiftManagement';
import GPSTracking from '../pages/gps/GPSTracking';
import Reports from '../features/reports/Reports';
import Unauthorized from '../pages/auth/Unauthorized';
import PasswordReset from '../pages/auth/PasswordReset';
import ResetPasswordConfirm from '../pages/auth/ResetPasswordConfirm';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import Layout from '../components/layout/Layout';

// 認証済みで /login にアクセスした際のリダイレクト先を決める。
// PrivateRoute が保存した state.from（元のURL）があればそこへ戻し、無ければダッシュボードへ。
const LoginRedirect: React.FC = () => {
  const location = useLocation();
  const from = (location.state as any)?.from;
  const to = from
    ? `${from.pathname || '/dashboard'}${from.search || ''}`
    : '/dashboard';
  return <Navigate to={to} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  // キャストユーザーはオーダー画面を専用のものに切り替える
  const isCast = String(user?.role || '').toUpperCase() === 'CAST';
  // ドライバーも専用のオーダー画面に切り替える
  const isDriver = String(user?.role || '').toUpperCase() === 'DRIVER';

  // カスタム404ページを作成（一時的）
  const NotFound = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>ページが見つかりません</h2>
      <p>申し訳ありませんが、このページは現在開発中です。</p>
    </div>
  );

  // 認証状態確認中はロード画面を表示
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100vh'
      }}>
        読み込み中...
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} 
      />
      {/* 未認証状態でのみアクセス可能なルート。
          認証済みでアクセスした場合は、戻り先(from)があればそこへ、無ければダッシュボードへ。 */}
      <Route path="/login" element={
        isAuthenticated ? <LoginRedirect /> : <Login />
      } />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/reset-password/:token" element={<ResetPasswordConfirm />} />
      
      {/* 認証が必要なルート */}
      <Route path="/*" element={
        <PrivateRoute>
          <Routes>
            {/* ダッシュボードページ - DashboardLayoutを使用 */}
            <Route path="/dashboard" element={
              <DashboardLayout>
                <Dashboard />
              </DashboardLayout>
            } />
            
            {/* その他のページ - 通常のLayoutを使用 */}
            <Route path="/*" element={
              <Layout>
                <Routes>
                  <Route path="/users" element={<UserListPage />} />
                  <Route path="/customers" element={<CustomerListPage />} />
                  <Route path="/customers/:userId/orders" element={<CustomerOrderHistory />} />
                  <Route path="/users/create" element={<UserCreatePage />} />
                  <Route path="/users/edit/:id" element={<EditUser />} />
                  <Route
                    path="/orders"
                    element={isCast ? <CastOrderList /> : isDriver ? <DriverOrderList /> : <OrderManagement />}
                  />
                  <Route path="/orders/create" element={<CreateOrder />} />
                  {/* キャスト専用のオーダー編集画面。キャスト以外は従来の一覧へ戻す */}
                  <Route
                    path="/orders/:id"
                    element={isCast ? <CastOrderEdit /> : <Navigate to="/orders" replace />}
                  />
                  <Route path="/shifts" element={<Shifts />} />
                  <Route path="/notification-settings" element={<NotificationSettings />} />
                  <Route path="/gps-tracking" element={<GPSTracking />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/settings" element={<SystemSettingsPage />} />
                  <Route path="/settings/system" element={<SystemSettingsPage />} />
                  <Route path="/system-settings" element={<SystemSettingsManagement />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </PrivateRoute>
      } />
    </Routes>
  );
};

export default AppRoutes;
